import { useMemo, type Dispatch, type SetStateAction } from 'react';

import { format } from 'date-fns';
import { Clock, FileText, Receipt, Trash2, Wallet } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import type { Commit, Expense, TimeEntry } from '@/pages/developer/types';

export type TimeExpenseTrackingCardProps = {
  totalHours: number;
  totalExpenses: number;

  timeDate: string;
  setTimeDate: (value: string) => void;
  timeHours: string;
  setTimeHours: (value: string) => void;
  timeHourlyRate: string;
  setTimeHourlyRate: (value: string) => void;
  handleAddTimeTicket: () => void;
  timeDescription: string;
  setTimeDescription: (value: string) => void;
  timeTickets: TimeEntry[];

  gitOrderDescription: string;
  setGitOrderDescription: (value: string) => void;
  timeMisc: string;
  setTimeMisc: (value: string) => void;
  timeTaxes: string;
  setTimeTaxes: (value: string) => void;
  createGitOrderFromSelectedCommits: () => void;
  gitOrders: TimeEntry[];

  commitsForSelectedTimeDate: Commit[];
  timeEntryCommitShas: string[];
  setTimeEntryCommitShas: Dispatch<SetStateAction<string[]>>;
  timeEntryMarkCommitsComplete: boolean;
  setTimeEntryMarkCommitsComplete: (value: boolean) => void;
  openWorkorderCommitsDialog: (timeEntryId: string) => void;
  deleteTimeEntry: (timeEntryId: string) => void;
  linkTimeTicketToGitOrder: (timeTicketId: string, gitOrderId: string | null) => void;

  expenseDate: string;
  setExpenseDate: (value: string) => void;
  expenseCategory: string;
  setExpenseCategory: (value: string) => void;
  expenseAmount: string;
  setExpenseAmount: (value: string) => void;
  expenseMisc: string;
  setExpenseMisc: (value: string) => void;
  expenseTaxes: string;
  setExpenseTaxes: (value: string) => void;
  handleAddExpense: () => void;
  expenseNotes: string;
  setExpenseNotes: (value: string) => void;
  expenses: Expense[];
};

export function TimeExpenseTrackingCard({
  totalHours,
  totalExpenses,
  timeDate,
  setTimeDate,
  timeHours,
  setTimeHours,
  timeHourlyRate,
  setTimeHourlyRate,
  handleAddTimeTicket,
  timeDescription,
  setTimeDescription,
  timeTickets,
  gitOrderDescription,
  setGitOrderDescription,
  timeMisc,
  setTimeMisc,
  timeTaxes,
  setTimeTaxes,
  createGitOrderFromSelectedCommits,
  gitOrders,
  commitsForSelectedTimeDate,
  timeEntryCommitShas,
  setTimeEntryCommitShas,
  timeEntryMarkCommitsComplete,
  setTimeEntryMarkCommitsComplete,
  openWorkorderCommitsDialog,
  deleteTimeEntry,
  linkTimeTicketToGitOrder,
  expenseDate,
  setExpenseDate,
  expenseCategory,
  setExpenseCategory,
  expenseAmount,
  setExpenseAmount,
  expenseMisc,
  setExpenseMisc,
  expenseTaxes,
  setExpenseTaxes,
  handleAddExpense,
  expenseNotes,
  setExpenseNotes,
  expenses,
}: TimeExpenseTrackingCardProps) {
  const normalizedSelectedCommitShas = new Set(timeEntryCommitShas.map((sha) => sha.trim()).filter(Boolean));

  const combinedEntries = useMemo(() => {
    const entries = [...timeTickets, ...gitOrders];
    return entries.sort((a, b) => {
      const aTime = new Date(a.date).getTime();
      const bTime = new Date(b.date).getTime();
      if (Number.isNaN(aTime) || Number.isNaN(bTime)) return 0;
      return bTime - aTime;
    });
  }, [gitOrders, timeTickets]);

  const isTimeTicket = (entry: TimeEntry) => entry.kind === 'time-ticket' || (!entry.kind && !(entry.commitShas?.length || entry.commitRate || entry.misc || entry.taxes || entry.invoiceJournalEntryId));

  return (
    <Card className="mb-8">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="w-5 h-5" />
          Time & Expense Tracking
        </CardTitle>
        <CardDescription>Log billable hours and expenses for the Mysttech Creations engagement.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-lg border border-border/60 bg-muted/20 p-4">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Hours</p>
                <p className="text-2xl font-bold text-foreground">{totalHours.toFixed(1)}</p>
              </div>
              <Clock className="w-6 h-6 text-primary" />
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Input type="date" value={timeDate} onChange={(e) => setTimeDate(e.target.value)} />
                <Input
                  type="number"
                  min="0"
                  step="0.25"
                  placeholder="Hours"
                  value={timeHours}
                  onChange={(e) => setTimeHours(e.target.value)}
                />
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Rate ($/hr)"
                  value={timeHourlyRate}
                  onChange={(e) => setTimeHourlyRate(e.target.value)}
                />
                <Button className="w-full gap-2" onClick={handleAddTimeTicket}>
                  <Clock className="w-4 h-4" />
                  Add time ticket
                </Button>
              </div>
              <Input
                placeholder="Time ticket notes"
                value={timeDescription}
                onChange={(e) => setTimeDescription(e.target.value)}
              />

              {timeDate && (
                <div className="rounded-lg border border-border/60 bg-muted/10 p-3 space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Git orders are created from commits. Select commits, then create a Git order. Selected commits will be marked{' '}
                    <span className="font-medium">assigned</span>, and you can optionally mark them <span className="font-medium">completed</span>.
                  </p>

                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={timeEntryMarkCommitsComplete}
                      onCheckedChange={(checked) => setTimeEntryMarkCommitsComplete(Boolean(checked))}
                    />
                    <span className="text-xs">Mark selected commits completed</span>
                  </div>

                  {commitsForSelectedTimeDate.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No commits found for this date.</p>
                  ) : (
                    <div className="max-h-[180px] overflow-y-auto rounded-md border border-border bg-background">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/30">
                            <TableHead className="w-[44px]">&nbsp;</TableHead>
                            <TableHead>Commit</TableHead>
                            <TableHead>Message</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {commitsForSelectedTimeDate.map((commit) => {
                            const checked = normalizedSelectedCommitShas.has(commit.sha);
                            return (
                              <TableRow key={commit.sha}>
                                <TableCell>
                                  <Checkbox
                                    checked={checked}
                                    onCheckedChange={(next) => {
                                      const shouldSelect = Boolean(next);
                                      setTimeEntryCommitShas((prev) => {
                                        const nextSet = new Set(prev.map((sha) => sha.trim()).filter(Boolean));
                                        if (shouldSelect) nextSet.add(commit.sha);
                                        else nextSet.delete(commit.sha);
                                        return Array.from(nextSet);
                                      });
                                    }}
                                  />
                                </TableCell>
                                <TableCell className="text-xs font-mono">{commit.sha.slice(0, 7)}</TableCell>
                                <TableCell className="text-xs text-muted-foreground max-w-[360px] truncate" title={commit.message}>
                                  {commit.message}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}

                  <div className="pt-2 space-y-2">
                    <Input
                      placeholder="Git order description (optional)"
                      value={gitOrderDescription}
                      onChange={(e) => setGitOrderDescription(e.target.value)}
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="Git order misc"
                        value={timeMisc}
                        onChange={(e) => setTimeMisc(e.target.value)}
                      />
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="Git order taxes"
                        value={timeTaxes}
                        onChange={(e) => setTimeTaxes(e.target.value)}
                      />
                    </div>
                    <Button
                      type="button"
                      className="w-full"
                      variant="outline"
                      disabled={normalizedSelectedCommitShas.size === 0}
                      onClick={createGitOrderFromSelectedCommits}
                    >
                      Create Git order from selected commits
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <div className="rounded-lg border border-border overflow-hidden">
              <div className="sm:hidden">
                {combinedEntries.length === 0 ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">No time tickets or Git orders yet.</div>
                ) : (
                  <div className="divide-y divide-border">
                    {combinedEntries.map((entry) => (
                      <div key={entry.id} className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-medium">{format(new Date(entry.date), 'MMM d, yyyy')}</p>
                            <p className="mt-1 text-xs text-muted-foreground break-words">{entry.description || '—'}</p>
                            <p className="mt-2 text-xs text-muted-foreground">
                              {isTimeTicket(entry)
                                ? `Hours ${entry.hours.toFixed(2)} @ $${(typeof entry.hourlyRate === 'number' && Number.isFinite(entry.hourlyRate) ? entry.hourlyRate : 65).toFixed(2)}/hr`
                                : `Hours ${Number.isFinite(entry.hours) ? entry.hours.toFixed(2) : '—'} · Commits ${(entry.commitShas ?? []).length || '—'} · Misc ${typeof entry.misc === 'number' && entry.misc ? entry.misc.toFixed(2) : '—'} · Taxes ${typeof entry.taxes === 'number' && entry.taxes ? entry.taxes.toFixed(2) : '—'}`}
                            </p>
                            <div className="mt-2">
                              <Badge variant="outline" className="text-primary border-primary/40">
                                {entry.stage === 'invoiced' || entry.invoiceJournalEntryId ? 'Closed' : isTimeTicket(entry) ? 'Time ticket' : 'Git order'}
                              </Badge>
                            </div>

                            {isTimeTicket(entry) ? (
                              <div className="mt-2">
                                <select
                                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                  value={(entry.gitOrderId ?? '').trim()}
                                  onChange={(e) => linkTimeTicketToGitOrder(entry.id, e.target.value ? e.target.value : null)}
                                >
                                  <option value="">Unlinked (no Git order)</option>
                                  {gitOrders.map((order) => (
                                    <option key={order.id} value={order.id}>
                                      {format(new Date(order.date), 'MMM d')} · {order.description || order.id.slice(0, 7)}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            ) : null}
                          </div>
                        </div>

                        {(isTimeTicket(entry) ? entry.stage !== 'invoiced' && !entry.invoiceJournalEntryId : true) ? (
                          <div className="mt-3">
                            <div className="flex gap-2">
                              {!isTimeTicket(entry) ? (
                                <Button
                                  className="flex-1"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openWorkorderCommitsDialog(entry.id)}
                                >
                                  Git order
                                </Button>
                              ) : (
                                <div className="flex-1" />
                              )}
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                aria-label="Delete entry"
                                onClick={() => deleteTimeEntry(entry.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="hidden sm:block">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30">
                      <TableHead>Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Hours</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Misc</TableHead>
                      <TableHead className="text-right">Taxes</TableHead>
                      <TableHead>Stage</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {combinedEntries.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground py-6">
                          No time tickets or Git orders yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      combinedEntries.map((entry) => (
                        <TableRow key={entry.id}>
                          <TableCell className="text-sm">{format(new Date(entry.date), 'MMM d, yyyy')}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {isTimeTicket(entry) ? 'Time ticket' : 'Git order'}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {Number.isFinite(entry.hours) ? entry.hours.toFixed(2) : '—'}
                            {isTimeTicket(entry) && (
                              <div className="text-[11px] font-normal text-muted-foreground">@ ${((typeof entry.hourlyRate === 'number' && Number.isFinite(entry.hourlyRate) ? entry.hourlyRate : 65)).toFixed(2)}/hr</div>
                            )}
                          </TableCell>
                          <TableCell className="max-w-[220px] truncate" title={entry.description}>
                            {entry.description}
                          </TableCell>
                          <TableCell className="text-right text-xs font-mono text-muted-foreground">
                            {typeof entry.misc === 'number' && entry.misc ? entry.misc.toFixed(2) : '—'}
                          </TableCell>
                          <TableCell className="text-right text-xs font-mono text-muted-foreground">
                            {typeof entry.taxes === 'number' && entry.taxes ? entry.taxes.toFixed(2) : '—'}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-primary border-primary/40">
                                {entry.stage === 'invoiced' || entry.invoiceJournalEntryId ? 'Closed' : isTimeTicket(entry) ? 'Time ticket' : 'Git order'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {(isTimeTicket(entry) ? entry.stage !== 'invoiced' && !entry.invoiceJournalEntryId : true) ? (
                              <div className="flex justify-end gap-2">
                                {!isTimeTicket(entry) ? (
                                  <Button variant="outline" size="sm" onClick={() => openWorkorderCommitsDialog(entry.id)}>
                                    Git order
                                  </Button>
                                ) : (
                                  <select
                                    className="flex h-9 rounded-md border border-input bg-background px-2 text-xs"
                                    value={(entry.gitOrderId ?? '').trim()}
                                    onChange={(e) => linkTimeTicketToGitOrder(entry.id, e.target.value ? e.target.value : null)}
                                  >
                                    <option value="">Unlinked</option>
                                    {gitOrders.map((order) => (
                                      <option key={order.id} value={order.id}>
                                        {format(new Date(order.date), 'MMM d')} · {order.description || order.id.slice(0, 7)}
                                      </option>
                                    ))}
                                  </select>
                                )}
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="icon"
                                  aria-label="Delete entry"
                                  onClick={() => deleteTimeEntry(entry.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-lg border border-border/60 bg-muted/20 p-4">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Expenses</p>
                <p className="text-2xl font-bold text-foreground">${totalExpenses.toFixed(2)}</p>
              </div>
              <Wallet className="w-6 h-6 text-primary" />
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Input type="date" value={expenseDate} onChange={(e) => setExpenseDate(e.target.value)} />
                <Input placeholder="Category" value={expenseCategory} onChange={(e) => setExpenseCategory(e.target.value)} />
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Amount"
                  value={expenseAmount}
                  onChange={(e) => setExpenseAmount(e.target.value)}
                />
                <Button className="w-full gap-2" onClick={handleAddExpense}>
                  <Receipt className="w-4 h-4" />
                  Add expense
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Misc"
                  value={expenseMisc}
                  onChange={(e) => setExpenseMisc(e.target.value)}
                />
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Taxes"
                  value={expenseTaxes}
                  onChange={(e) => setExpenseTaxes(e.target.value)}
                />
              </div>
              <Input placeholder="Notes (optional)" value={expenseNotes} onChange={(e) => setExpenseNotes(e.target.value)} />
            </div>
            <div className="rounded-lg border border-border overflow-hidden">
              <div className="sm:hidden">
                {expenses.length === 0 ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">No expenses logged yet.</div>
                ) : (
                  <div className="divide-y divide-border">
                    {expenses.map((entry) => (
                      <div key={entry.id} className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-medium">{format(new Date(entry.date), 'MMM d, yyyy')}</p>
                            <p className="mt-1 text-xs text-muted-foreground">{entry.category || '—'}</p>
                            <p className="mt-2 text-xs text-muted-foreground break-words">{entry.notes || '—'}</p>
                            <p className="mt-2 text-xs text-muted-foreground">
                              Misc {typeof entry.misc === 'number' && entry.misc ? entry.misc.toFixed(2) : '—'} · Taxes{' '}
                              {typeof entry.taxes === 'number' && entry.taxes ? entry.taxes.toFixed(2) : '—'}
                            </p>
                            {entry.attachmentName ? (
                              <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                                <FileText className="w-3 h-3" />
                                <span className="truncate">{entry.attachmentName}</span>
                              </div>
                            ) : null}
                          </div>
                          <div className="shrink-0 text-right">
                            <p className="text-sm font-semibold">${entry.amount.toFixed(2)}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="hidden sm:block">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30">
                      <TableHead>Date</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">Misc</TableHead>
                      <TableHead className="text-right">Taxes</TableHead>
                      <TableHead>Attachment</TableHead>
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {expenses.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground py-6">
                          No expenses logged yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      expenses.map((entry) => (
                        <TableRow key={entry.id}>
                          <TableCell className="text-sm">{format(new Date(entry.date), 'MMM d, yyyy')}</TableCell>
                          <TableCell>{entry.category}</TableCell>
                          <TableCell className="text-right font-medium">${entry.amount.toFixed(2)}</TableCell>
                          <TableCell className="text-right text-xs font-mono text-muted-foreground">
                            {typeof entry.misc === 'number' && entry.misc ? entry.misc.toFixed(2) : '—'}
                          </TableCell>
                          <TableCell className="text-right text-xs font-mono text-muted-foreground">
                            {typeof entry.taxes === 'number' && entry.taxes ? entry.taxes.toFixed(2) : '—'}
                          </TableCell>
                          <TableCell>
                            {entry.attachmentName ? (
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <FileText className="w-3 h-3" />
                                <span>{entry.attachmentName}</span>
                              </div>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell className="max-w-[220px] truncate" title={entry.notes}>
                            {entry.notes || '—'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
